import type { NotificationPreferences } from '@/lib/api/common/notification-preferences-api'
'use server'

import { apiRequest, ApiResponse } from '../request'
import { PartnerPayoutScheduleType } from '../common/interfaces/partner.interface'

export interface Supplier {
    /**
     * Canais por onde esta entidade aceita ser notificada. Ausente = nada: só recebe
     * quem consentiu. Ver `notification-preferences-api.ts`.
     */
    notificationPreferences?: NotificationPreferences | null
    id: number
    name: string
    tradingName?: string
    cnpj?: string
    stateRegistration?: string
    email?: string
    phone?: string
    website?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    active: boolean
    payoutScheduleType?: PartnerPayoutScheduleType
    payoutFixedDay?: number | null
    payoutDaysAfterAppointment?: number | null
    organizationId: number
    typeId?: number | null
    type?: { id: number; name: string } | null
    metadata?: Array<{ key: string; value: string }>
    createdAt: string
    updatedAt: string
    batchesCount?: number
    organization?: {
        id: number
        name: string
        tradingName?: string
    }
}

export interface CreateSupplierData {
    name: string
    tradingName?: string
    cnpj?: string
    stateRegistration?: string
    typeId?: number | null
    email?: string
    phone?: string
    website?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    active?: boolean
    payoutScheduleType?: PartnerPayoutScheduleType
    payoutFixedDay?: number | null
    payoutDaysAfterAppointment?: number | null
    organizationId: number
    metadata?: Array<{ key: string; value: string }>
}

export interface UpdateSupplierData {
    name?: string
    tradingName?: string
    cnpj?: string
    stateRegistration?: string
    typeId?: number | null
    email?: string
    phone?: string
    website?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    active?: boolean
    payoutScheduleType?: PartnerPayoutScheduleType
    payoutFixedDay?: number | null
    payoutDaysAfterAppointment?: number | null
    metadata?: Array<{ key: string; value: string }>
}

export async function getSuppliers(
    organizationId?: number,
): Promise<ApiResponse<Supplier[]>> {
    const params = organizationId ? { organizationId } : {}
    return apiRequest<Supplier[]>('suppliers', { params })
}

export async function getSuppliersByOrganization(
    organizationId: number,
    params?: {
        typeId?: number
        page?: number
        limit?: number
        search?: string
    },
): Promise<ApiResponse<Supplier[]>> {
    return apiRequest<Supplier[]>(`suppliers/organization/${organizationId}`, {
        params: params ?? {},
    })
}

export async function searchSuppliers(
    organizationId: number,
    searchTerm?: string,
): Promise<ApiResponse<Supplier[]>> {
    const params = searchTerm ? { q: searchTerm } : {}
    return apiRequest<Supplier[]>(`suppliers/organization/${organizationId}/search`, {
        params,
    })
}

export async function getSupplier(
    id: number,
): Promise<ApiResponse<Supplier>> {
    return apiRequest<Supplier>(`suppliers/${id}`, {})
}

export async function createSupplier(
    data: CreateSupplierData,
): Promise<ApiResponse<Supplier>> {
    return apiRequest<Supplier>('suppliers', { data }, 'post')
}

export async function updateSupplier(
    id: number,
    data: UpdateSupplierData,
): Promise<ApiResponse<Supplier>> {
    return apiRequest<Supplier>(`suppliers/${id}`, { data }, 'patch')
}

export async function deleteSupplier(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`suppliers/${id}`, {}, 'delete')
}

export async function toggleSupplierActive(
    id: number,
): Promise<ApiResponse<Supplier>> {
    return apiRequest<Supplier>(`suppliers/${id}/toggle-active`, {}, 'patch')
}
